/* eslint-disable jsx-a11y/anchor-is-valid */
import { ReactElement, useState, useEffect, useContext } from 'react';
import { iToolTips, iFormNavActions } from '@interfaces/interfaces';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Modal } from '@components';
import { useFormikContext } from 'formik';
import { CLICK_PLUS_ICON } from '@services/ErrorMessages';
import VicFinesContext from '@pages/forms/VicFinesFormContext';
import { navigateToNextStep } from '@utils/Navigation';
import { FormType } from '@constants';
import styles from './FormNavFooter.module.scss';

interface iNavFooter {
  totalPages: number;
  submitPage: number;
  submit: () => Promise<boolean>;
  tooltips: iToolTips;
  formNavActions?: iFormNavActions;
  displayErrorsCount(errorCount: number, touchedCount: number): void;
  pageValidationKeys?: any;
  formType?: FormType;
  firstPageNo: number;
  isDisabled?: boolean;
  disableThePage?: number;
}

function FormNavFooter({ totalPages, submitPage, submit, tooltips, formNavActions, displayErrorsCount, pageValidationKeys = [], formType = FormType.THIRDPARTY, firstPageNo, isDisabled = false, disableThePage = -1 }: iNavFooter): ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  let errorMessages: any[] = [];
  const formikContext = useFormikContext();
  const { apiError, clearApiError } = useContext(VicFinesContext);
  const { values, errors, touched, setFieldTouched, setFieldValue }: { values: any; errors: any; touched: any; setFieldTouched: any; setFieldValue: any } = formikContext;

  const [modal, setModal] = useState({
    show: false,
    title: '',
    content: '',
    contentClass: '',
  });

  const getCurrentPage = (): number => {
    const pageNoRegex = /\/step(\d*)/;
    const pageNo = location.pathname.match(pageNoRegex);
    if (!pageNo) {
      return -1; // Assume we are on root page
    }
    return Number(pageNo[1]);
  };
  const disableSpecificPage = (pageNum: number | undefined) => pageNum && formType === FormType.NOMINATION && values.isThePersonNamed === 'false' && getCurrentPage() === pageNum;
  const getNextPageSegment = (direction: 'next' | 'prev') => {
    const pageNo = getCurrentPage();
    let nextPageNo: number;
    if (direction === 'next') {
      nextPageNo = pageNo + 1;
    } else {
      nextPageNo = pageNo - 1;
    }
    // Extension form
    if (direction === 'next' && formType === FormType.EXTENSION && pageNo === 1) {
      nextPageNo = 15;
    }
    if (direction === 'next' && formType === FormType.EXTENSION && pageNo === 15) {
      nextPageNo = 2;
    }
    return `step${nextPageNo}`;
  };

  const getNextButtonText = (): string => {
    if (getCurrentPage() === submitPage) {
      return 'Submit';
    }
    if (values.SaveYesNo === 'true') return 'Save and continue';
    return 'Continue';
  };

  const isNextBtnDisabled = ():boolean => {
    if ((getCurrentPage() === submitPage && isDisabled) || disableSpecificPage(disableThePage)) {
      return true;
    }
    if (formType === FormType.REVIEW && values.WasSomeoneElseDrivingAtTimeOfOffence === 'true') return true;
    return false;
  };
  // filter errors
  errorMessages = Object.keys(errors).filter((key) => pageValidationKeys.includes(key));

  const goNext = async () => {
    if (formType === 'Review' && values.SelectedObligationsStep12.length < 1 && values.areThereAnyAdditionalFines === 'listFinesManually' && values.obligationNumberToIncludeStep12) {
      formikContext.setErrors({ obligationNumberToIncludeStep12: CLICK_PLUS_ICON });
      displayErrorsCount(errorMessages.length, Object.keys(touched).length);
      return;
    }

    if (pageValidationKeys.length !== 0) {
      displayErrorsCount(errorMessages.length, Object.keys(touched).length);
    }

    if (getCurrentPage() === submitPage) {
      if (errorMessages && errorMessages.length > 0 && pageValidationKeys.length !== 0) {
        pageValidationKeys.map((key: any) => setFieldTouched(key));
        return;
      }

      const submitSuccesfull = await submit();
      if (submitSuccesfull === false) {
        const url = `${location.pathname.split('/').slice(0, -1).join('/')}/error`;
        navigate(url);
        return;
      }
    }

    if ((errorMessages.length > 0 || Object.keys(touched).length === 0) && pageValidationKeys.length !== 0) {
      window.scrollTo(0, 0);
      pageValidationKeys.map((key: any) => setFieldTouched(key));
      return;
    }

    // If we have an override for this page then action it before navigation
    // handling FVS/ scenario
    const locationPath = location.pathname.slice(-1) === '/' ? location.pathname.slice(0, -1) : location.pathname;
    const lastSegment = locationPath.substring(location.pathname.lastIndexOf('/') + 1);
    if (formNavActions && formNavActions[lastSegment]) {
      await formNavActions[lastSegment].next(formikContext);
      return;
    }

    const pageSegment = getNextPageSegment('next');
    let url = `${locationPath.split('/').slice(0, -1).join('/')}/${pageSegment}`;
    // Edge case where we are on form root page but don't have a /pagex segment
    if (pageSegment === 'step0') {
      url = `${locationPath}/step${firstPageNo + 1}`;
    }
    window.scrollTo(0, 0);
    navigate(url);
  };

  const clearFieldsWhenBack = () => {
    setFieldValue('OBLIGATION_NUMBER', '');
    setFieldTouched('OBLIGATION_NUMBER');
    setFieldValue('OFFENCE_DATE', '');
    setFieldTouched('OFFENCE_DATE');
    setFieldValue('HEARING_DATE', '');
    setFieldTouched('HEARING_DATE');
    setFieldValue('DRIVER_LICENCE', '');
    setFieldTouched('DRIVER_LICENCE');
    setFieldValue('VEHICLE_REGISTRATION', '');
    setFieldTouched('VEHICLE_REGISTRATION');
    setFieldValue('DOB', '');
    setFieldTouched('DOB');
  };

  useEffect(() => {
    const enterListener = (event: any) => {
      if ((event.code === 'Enter' || event.code === 'NumpadEnter') && (formType !== FormType.INFRINGEMENTEXTENSION && formType !== FormType.INSTALMENT && formType !== FormType.NOMINATION) && formType !== FormType.REVIEW && formType !== FormType.FAMILYVIOLENCESCHEME) {
        event.preventDefault();
        goNext();
      }
    };
    document.addEventListener('keydown', enterListener);
    return () => {
      document.removeEventListener('keydown', enterListener);
    };
  }, [pageValidationKeys]);

  return (
    <>
      <Modal close={() => setModal({ ...modal, show: false })} {...modal} />
      <div className={styles.navigationContainer}>
        <div className={styles.navigationRow}>
          {!(getCurrentPage() === 7 && !values.isStep7ContinueApplicationClicked && formType === FormType.INSTALMENT) && (
            <div className={styles.buttons}>
              {!(getCurrentPage() === -1 || getCurrentPage() === firstPageNo
              || (formType === FormType.FAMILYVIOLENCESCHEME
                && getCurrentPage() === 3)
              || (formType === FormType.INFRINGEMENTEXTENSION
                && getCurrentPage() === 3)
              || (formType === FormType.INSTALMENT
                && (getCurrentPage() === 4 || (getCurrentPage() === 5 && values.SaveYesNo === 'true')))
              || (formType === FormType.NOMINATION
                && (getCurrentPage() === 3 || (getCurrentPage() === 4 && values.SaveYesNo === 'true' && values.isDebtorTypeIndividual)
              || (getCurrentPage() === 5 && !values.CanSave && !values.isDebtorTypeIndividual)
              || (getCurrentPage() === 4 && !values.CanSave && values.isDebtorTypeIndividual)
              || (getCurrentPage() === 5 && values.SaveYesNo === 'true' && !values.isDebtorTypeIndividual)
              || (values.CheckAndConfirmPageLoaded && (getCurrentPage() !== 12 && getCurrentPage() !== 13))))
              || (formType === FormType.REVIEW
                && (getCurrentPage() === 3
                || ((getCurrentPage() !== 13 && getCurrentPage() !== 14) && values.CheckAndConfirmPageLoaded)
                || (getCurrentPage() === 4 && (values.isDebtorTypeCompany || !(values.isNominatable || values.Status === 'PenaltyReminder')) && values.isInternalReviewStage)
                || (values.isDebtorTypeCompany && getCurrentPage() === 6 && !values.CanSave)
                || (values.SaveYesNo === 'true'
                  && ((values.isDebtorTypeIndividual && getCurrentPage() === 5)
                  || (values.isDebtorTypeCompany && getCurrentPage() === 6)))
                || (getCurrentPage() === 5 && !values.CanSave && (values.isDebtorTypeCompany || !(values.isNominatable || values.Status === 'PenaltyReminder')) && values.isInternalReviewStage)
                || (getCurrentPage() === 6 && !values.CanSave && (values.isDebtorTypeCompany || !(values.isNominatable || values.Status === 'PenaltyReminder')) && values.isInternalReviewStage)
                ))
              )
            && (
              <Button
                className={styles.backBtn}
                onClick={() => {
                    if (formType === FormType.COURT || formType === FormType.FAMILYVIOLENCESCHEME || formType === FormType.EXTENSION || formType === FormType.INFRINGEMENTEXTENSION || formType === FormType.NOMINATION) {
                      if (pageValidationKeys.length !== 0) {
                        displayErrorsCount(0, 1);
                      }
                      if (apiError?.Error && clearApiError) clearApiError();
                      if ((formType === FormType.FAMILYVIOLENCESCHEME || formType === FormType.INFRINGEMENTEXTENSION || formType === FormType.NOMINATION) && (getCurrentPage() === 1 || getCurrentPage() === 2)) {
                        setFieldValue('OBLIGATION_NUMBER', '');
                        setFieldTouched('OBLIGATION_NUMBER');
                        setFieldValue('OFFENCE_DATE', '');
                        setFieldValue('HEARING_DATE', '');
                        setFieldValue('DRIVER_LICENCE', '');
                        setFieldValue('VEHICLE_REGISTRATION', '');
                        setFieldValue('DOB', '');
                      }
                      const locationPath = location.pathname.slice(-1) === '/' ? location.pathname.slice(0, -1) : location.pathname;
                      if (
                        formType === FormType.EXTENSION
                        && (getCurrentPage() === 2 || getCurrentPage() === 15 || getCurrentPage() === 4 || getCurrentPage() === 3)
                        && getCurrentPage() !== 1
                      ) {
                        clearFieldsWhenBack();
                        if (getCurrentPage() === 4) {
                          setFieldValue('SaveYesNo', 'false');
                        }
                        const url = `${locationPath.split('/').slice(0, -1).join('/')}/step1`;
                        navigate(url, { replace: true });
                        window.scrollTo(0, 0);
                      } else if (formType === FormType.EXTENSION && getCurrentPage() === 1) {
                        navigate(`${locationPath.split('/').slice(0, -1).join('/')}/step0`, { replace: true });
                        window.scrollTo(0, 0);
                      } else {
                      navigate(-1);
                      window.scrollTo(0, 0);
                      }
                  } else if (formType === FormType.INSTALMENT) {
                    if ((getCurrentPage() === 1 || getCurrentPage() === 2)) {
                      setFieldValue('OBLIGATION_NUMBER', '');
                      setFieldTouched('OBLIGATION_NUMBER');
                      setFieldValue('OFFENCE_DATE', '');
                      setFieldValue('HEARING_DATE', '');
                      setFieldValue('DRIVER_LICENCE', '');
                      setFieldValue('VEHICLE_REGISTRATION', '');
                      setFieldValue('DOB', '');
                    }
                    if (getCurrentPage() === 3) {
                      setFieldValue('OFFENCE_DATE', '');
                      setFieldValue('HEARING_DATE', '');
                      setFieldValue('DRIVER_LICENCE', '');
                      setFieldValue('VEHICLE_REGISTRATION', '');
                      setFieldValue('DOB', '');
                    }
                    // if (getCurrentPage() === 1) {
                    //   navigate(-1);
                    //   window.scrollTo(0, 0);
                    // }
                    // const locationPath = location.pathname.slice(-1) === '/' ? location.pathname.slice(0, -1) : location.pathname;
                    // if (getCurrentPage() === 3) {
                    //   if ((values.isDebtorTypeCompany && values.isNominatable)) {
                    //     navigateToNextStep(navigate, location, 'previous');
                    //   } else {
                    //     setFieldValue('OBLIGATION_NUMBER', '');
                    //     setFieldTouched('OBLIGATION_NUMBER');
                    //     navigate(`${locationPath.split('/').slice(0, -1).join('/')}/step1`, { replace: true });
                    //     window.scrollTo(0, 0);
                    //   }
                    // }
                    // if (getCurrentPage() === 8) {
                    //   if (values.NoInfoApplies) {
                    //     navigate(`${locationPath.split('/').slice(0, -1).join('/')}/step6`, { replace: true });
                    //   } else {
                    //     navigateToNextStep(navigate, location, 'previous');
                    //   }
                    // }
                    // if (getCurrentPage() === 9) {
                    //   if ((values.isDebtorTypeCompany && values.NoInfoApplies)) {
                    //     navigate(`${locationPath.split('/').slice(0, -1).join('/')}/step6`, { replace: true });
                    //   } else if (values.isDebtorTypeCompany && !values.NoInfoApplies) {
                    //     navigate(`${locationPath.split('/').slice(0, -1).join('/')}/step7`, { replace: true });
                    //   } else {
                    //     navigateToNextStep(navigate, location, 'previous');
                    //   }
                    // }
                    navigate(-1);
                    window.scrollTo(0, 0);
                  } else if (formType === FormType.REVIEW) {
                    if (pageValidationKeys.length !== 0) {
                      displayErrorsCount(0, 1);
                    }
                    if ((getCurrentPage() === 1 || getCurrentPage() === 2)) {
                      setFieldValue('OBLIGATION_NUMBER', '');
                      setFieldTouched('OBLIGATION_NUMBER');
                      setFieldValue('OFFENCE_DATE', '');
                      setFieldValue('HEARING_DATE', '');
                      setFieldValue('DRIVER_LICENCE', '');
                      setFieldValue('VEHICLE_REGISTRATION', '');
                      setFieldValue('DOB', '');
                    }
                    navigate(-1);
                    window.scrollTo(0, 0);
                  } else navigateToNextStep(navigate, location, 'previous');
                }}
                color="white"
                size="small">
                {/* Link nested to ensure Google Tag Manager logs an event (Tied to a link with back text) */}
                <a
                  href="#"
                  id="go-back"
                  className={styles.goBack}
                  onClick={(e) => {
                    e.preventDefault();
                  }}>
                  Back
                </a>
              </Button>
            )}
              {getCurrentPage() !== totalPages && (
                <Button className={getCurrentPage() !== 0 && getCurrentPage() !== -1 ? styles.continueBtn : undefined} onClick={goNext} isDisabled={isNextBtnDisabled()}>
                  {getNextButtonText()}
                </Button>
              )}
            </div>
          )}
          <div className={styles.helpLinks}>
            <button
              type="button"
              onClick={() => {
                let content = tooltips.needhelponformglobal;
                let title = '';
                const regex = /<h4>(.*)<\/h4>\n((.|\n|)*>)/;
                const modalContent = tooltips.needhelponformglobal.match(regex);
                if (modalContent && modalContent.length >= 3) {
                  [, title, content] = modalContent;
                }
                setModal({
                  show: true,
                  title,
                  content,
                  contentClass: '',
                });
              }}>
              Need help?
            </button>
            <button
              type="button"
              onClick={() => {
                let content = tooltips.needassistanceonformglobal;
                let title = '';
                const regex = /<h4>(.*)<\/h4>\n((.|\n|)*>)/;
                const modalContent = tooltips.needassistanceonformglobal.match(regex);
                if (modalContent && modalContent.length >= 3) {
                  [, title, content] = modalContent;
                }
                setModal({
                  show: true,
                  title,
                  content,
                  contentClass: styles.modalLegal,
                });
              }}>
              Need legal assistance?
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default FormNavFooter;
